/**
 * `useMyTasks` — fetches every input the My Tasks page needs (current
 * user's assignee mapping, WBS rows, migration tasks) and folds them
 * into the four tab buckets via `buildMyTasksBuckets`.
 *
 * The hook keeps Firestore plumbing (subscriptions, loading flags) at
 * the edge so the page renders straight from the returned buckets and
 * the unit tests can hit `buildMyTasksBuckets` directly without booting
 * a hook environment.
 */
import { useMemo } from 'react';
import { useAuthState } from './useAuth';
import {
  useAssigneeUserMappings,
  useTasks,
} from './useFirestoreData';
import { useWbsItems } from './useWbs';
import {
  buildMyTasksBuckets,
  resolveMyAssigneeId,
  type MyTasksBuckets,
} from '../lib/myTasks';

export interface UseMyTasksResult {
  /** Tab-bucketed normalised rows. Empty buckets when not yet resolved. */
  buckets: MyTasksBuckets;
  /** `null` while loading OR if the user isn't mapped. Use `mapped` to disambiguate. */
  myAssigneeId: string | null;
  /** Stays `false` until the mapping listener has emitted at least once. */
  mappingsReady: boolean;
  /** Whether the current uid resolves to an assignee. False after mapping load
   *  is the cue to surface the "設定で自分をマッピング" CTA. */
  mapped: boolean;
  loading: boolean;
}

export function useMyTasks(
  projectId: string | null,
  now: Date,
): UseMyTasksResult {
  const { user } = useAuthState();
  const uid = user?.uid ?? null;
  const { data: mappings, loading: mappingsLoading } =
    useAssigneeUserMappings(projectId);
  const { data: wbsItems, loading: wbsLoading } = useWbsItems(projectId);
  const { data: tasks, loading: tasksLoading } = useTasks(projectId);

  const myAssigneeId = useMemo(
    () => resolveMyAssigneeId(uid, mappings),
    [uid, mappings],
  );

  const buckets = useMemo(
    () =>
      buildMyTasksBuckets({
        myAssigneeId,
        wbsItems,
        tasks,
        now,
      }),
    [myAssigneeId, wbsItems, tasks, now],
  );

  return {
    buckets,
    myAssigneeId,
    mappingsReady: !mappingsLoading,
    mapped: myAssigneeId !== null,
    loading: mappingsLoading || wbsLoading || tasksLoading,
  };
}
